'use client';

import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * The phone bottom sheet every buy surface on this site opens: the offers
 * sheet ("Where to buy") and the kit checkout ("Get the whole kit").
 *
 * Extracted from `components/products/links/offers/offers-sheet.tsx`
 * (2026-09-28), where each of these was proven on its own:
 *  - a PORTAL to document.body, so no ancestor's stacking context or
 *    overflow can clip it, and `fixed` is immune to the product card's
 *    100vh scroll-snap and iOS's collapsing address bar;
 *  - STACKING 60/61, above the floating search button and the top menu
 *    (both z-50). A surface that declares `aria-modal="true"` and puts a
 *    scrim over the page has to actually be on top of it. The kit sheet sat
 *    at 45/46 and the search button covered its total and its email button
 *    (QA 2026-09-28, explorer-floating-search-button-covers-kit-sheet);
 *  - focus moves in on open, is trapped inside, and goes back to the
 *    trigger on close; Escape closes;
 *  - the phone's Back button closes the sheet instead of leaving the page
 *    (attachSheetHistoryEntry). The kit sheet had none, so Back switched the
 *    price tier under an open sheet (explorer-phone-back-button-ignores-buy-sheet).
 */

const FOCUSABLE =
  'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** Tailwind's `sm` in this repo (tailwind.config.js `theme.screens`) is 840px. */
const SHEET_MAX_WIDTH = 840;

/**
 * Only ever evaluated for a sheet the reader has already opened — i.e. after
 * hydration — so reading the viewport during render cannot desync SSR markup.
 */
export const isSheetViewport = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  !window.matchMedia(`(min-width: ${SHEET_MAX_WIDTH}px)`).matches;

/** Marks the history entry an open sheet pushed (the offers sheet's key since 2026-09-27). */
export const SHEET_HISTORY_KEY = '__iaiOffersSheet';

let sheetEntrySeq = 0;

type SheetHistoryWindow = Pick<Window, 'addEventListener' | 'removeEventListener' | 'setTimeout'> & {
  history: Pick<History, 'state' | 'pushState' | 'replaceState' | 'back'>;
};

const sheetEntryId = (state: unknown): unknown =>
  state && typeof state === 'object'
    ? (state as Record<string, unknown>)[SHEET_HISTORY_KEY]
    : undefined;

/**
 * Gives the open sheet its own history entry, so the phone's Back button (or
 * the back swipe) closes the sheet rather than leaving the page. Returns the
 * teardown to call when the sheet unmounts.
 *
 * The entry copies the current `history.state` (Next's router keeps its tree
 * there) and keeps the current URL, so Back lands on exactly the page the
 * router already has. When the sheet closes any other way (its Back button,
 * the scrim, Escape, a row click) the entry is popped again with
 * `history.back()`, so a later Back is never "eaten" by a sheet that is no
 * longer open. It is popped only while it is still the current entry: after a
 * navigation away the entry is no longer on top and is left alone.
 */
export function attachSheetHistoryEntry(win: SheetHistoryWindow, onClose: () => void): () => void {
  const id = ++sheetEntrySeq;
  const current = win.history.state;
  const base = current && typeof current === 'object' ? (current as Record<string, unknown>) : {};
  const entry = { ...base, [SHEET_HISTORY_KEY]: id };
  // Already on a sheet entry (React's dev double-mount re-runs this before the
  // previous run's pop has happened): take it over instead of stacking a second.
  if (SHEET_HISTORY_KEY in base) win.history.replaceState(entry, '');
  else win.history.pushState(entry, '');

  let poppedByBrowser = false;
  const onPopState = () => {
    if (sheetEntryId(win.history.state) === id) return;
    poppedByBrowser = true;
    win.removeEventListener('popstate', onPopState);
    onClose();
  };
  win.addEventListener('popstate', onPopState);

  return () => {
    win.removeEventListener('popstate', onPopState);
    if (poppedByBrowser) return;
    // Deferred one task so a same-tick remount can claim the entry first.
    win.setTimeout(() => {
      if (sheetEntryId(win.history.state) === id) win.history.back();
    }, 0);
  };
}

function useSheetHistoryEntry(onClose: () => void): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.history?.pushState !== 'function') return;
    return attachSheetHistoryEntry(window, () => onCloseRef.current());
  }, []);
}

/* ------------------------------------------------------------------ *
 * "Is any sheet open?" — floating buttons hide while one is
 * ------------------------------------------------------------------ */

/** The sheets open right now, by id. */
const openSheets = new Map<string | symbol, number>();
const openSheetListeners = new Set<() => void>();
const notifyOpenSheets = () => openSheetListeners.forEach((listener) => listener());

const subscribeOpenSheets = (listener: () => void) => {
  openSheetListeners.add(listener);
  return () => {
    openSheetListeners.delete(listener);
  };
};

/**
 * True while any BottomSheet other than `exceptId` is open; false on the
 * server. A caller that owns a sheet passes its id so its own sheet, which is
 * still registered for one render after it closes, never hides its trigger
 * while focus is being handed back to it.
 */
export function useAnySheetOpen(exceptId?: string): boolean {
  return useSyncExternalStore(
    subscribeOpenSheets,
    () => [...openSheets.keys()].some((id) => id !== exceptId),
    () => false,
  );
}

/* ------------------------------------------------------------------ *
 * The sheet
 * ------------------------------------------------------------------ */

type BottomSheetProps = {
  open: boolean;
  onClose: () => void;
  /** Names this sheet for useAnySheetOpen(exceptId). */
  id?: string;
  /** The dialog's accessible name. */
  label: string;
  /** Accessible name of the scrim, which closes the sheet when tapped. */
  scrimLabel: string;
  /** Focused when the sheet opens (the offers sheet's Back button); the dialog itself otherwise. */
  initialFocusRef?: RefObject<HTMLElement>;
  /** The sheet body's padding. */
  className: string;
  children: ReactNode;
};

/** Renders only while open, after hydration, on a phone-width viewport. */
export function BottomSheet({ open, ...sheet }: BottomSheetProps) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!open || !mounted || !isSheetViewport()) return null;

  return createPortal(<BottomSheetBody {...sheet} />, document.body);
}

function BottomSheetBody({
  id,
  onClose,
  label,
  scrimLabel,
  initialFocusRef,
  className,
  children,
}: Omit<BottomSheetProps, 'open'>) {
  const cardRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Remember the trigger BEFORE moving focus, and hand it back on unmount.
  // Runs once: re-running on a prop change would record our own button as the
  // trigger and trap focus inside a sheet that is going away.
  useEffect(() => {
    triggerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    (initialFocusRef?.current ?? cardRef.current)?.focus({ preventScroll: true });

    return () => {
      const trigger = triggerRef.current;
      // The trigger can re-render while the sheet is open (a live product
      // update), so the original node may be gone by the time we hand focus back.
      if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phone Back closes the sheet instead of leaving the page.
  useSheetHistoryEntry(onClose);

  // Escape closes; Tab cycles inside the card. Captured on the window rather
  // than the card so focus that has drifted outside still cannot escape.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const card = cardRef.current;
      if (!card) return;
      const items = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !card.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !card.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  useEffect(() => {
    const key = id ?? Symbol('bottom-sheet');
    openSheets.set(key, (openSheets.get(key) ?? 0) + 1);
    notifyOpenSheets();
    return () => {
      const left = (openSheets.get(key) ?? 1) - 1;
      if (left > 0) openSheets.set(key, left);
      else openSheets.delete(key);
      notifyOpenSheets();
    };
  }, [id]);

  return (
    <BottomSheetFrame
      onClose={onClose}
      label={label}
      scrimLabel={scrimLabel}
      className={className}
      cardRef={cardRef}
      focusable={!initialFocusRef}
    >
      {children}
    </BottomSheetFrame>
  );
}

/** The scrim and the card: the sheet's markup, stateless. */
export function BottomSheetFrame({
  onClose,
  label,
  scrimLabel,
  className,
  cardRef,
  focusable = false,
  children,
}: {
  onClose: () => void;
  label: string;
  scrimLabel: string;
  className: string;
  cardRef?: RefObject<HTMLDivElement>;
  /** The card takes focus itself when nothing inside is given it. */
  focusable?: boolean;
  children: ReactNode;
}) {
  return (
    <>
      <button
        type="button"
        aria-label={scrimLabel}
        onClick={onClose}
        className="fixed inset-0 z-[60] cursor-default bg-black/30 backdrop-blur-[1px] animate-fade-in motion-reduce:animate-none sm:hidden"
      />
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        {...(focusable ? { tabIndex: -1 } : {})}
        className={`fixed inset-x-0 bottom-0 z-[61] max-h-[82vh] overflow-y-auto rounded-t-2xl border border-zinc-200/70 bg-white/95 ${className} shadow-search-bar backdrop-blur-md animate-slide-up motion-reduce:animate-none dark:border-white/10 dark:bg-dark/95 dark:shadow-search-bar-dark sm:hidden`}
      >
        {children}
      </div>
    </>
  );
}
